import {icon} from '#core/utils/style-utils.js';
import {resolveBackground} from '#core/utils/color-utils.js';
import {merge} from '#core/utils/config-utils.js';

import {generateCloseButton, CLOSE_BUTTON_TYPES} from '#core/application/modules/tiles/close-button/index.js';
import {generateMiniEventItem} from '#core/application/modules/tiles/mini-event-item/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';
import {generateToggleButtonRow} from '#core/application/modules/tiles/button/toggle-button/index.js';

export function quickNavigationsFactory(PALETTE) {
    const NAVIGATION_CONTAINER_STYLE = {
        style: {
            padding: 0,
        },
    };
    const NAVIGATION_TITLE_CONTAINER_STYLE = {
        style: {
            ...resolveBackground(PALETTE.QUICK_NAVIGATIONS_TITLE_CONTAINER_BG),
            margin: '0 0 4px',
        },
    };
    const NAVIGATION_TITLE_COUNTRY_ICON_STYLE = {
        sportIcon: {
            icon: icon(PALETTE.QUICK_NAVIGATIONS_TITLE_ICON_COLOR),
        },
    };
    const NAVIGATION_TITLE_STYLE = {
        style: PALETTE.QUICK_NAVIGATIONS_TITLE_TEXT,
    };
    const NAVIGATION_ITEMS_CONTAINER_STYLE = {
        separator: {
            style: {
                color: PALETTE.QUICK_NAVIGATIONS_ITEMS_DIVIDER,
            },
        },
    };

    return {
        QuickNavigationPane: {
            dialog: {
                closeButton: generateCloseButton(PALETTE.CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
                dialogContent: {
                    style: {
                        ...resolveBackground(PALETTE.QUICK_NAVIGATIONS_CONTAINER_BG),
                        borderRadius: PALETTE.QUICK_NAVIGATIONS_BORDER.BORDER_RADIUS,
                    },
                },
            },
            popup: {
                container: {
                    style: {
                        ...resolveBackground(PALETTE.QUICK_NAVIGATIONS_CONTAINER_BG),
                        ...generateBorder(PALETTE.QUICK_NAVIGATIONS_BORDER),
                        overflow: 'hidden',
                    },
                },
            },
        },
        QuickEventsNavigation: {
            container: NAVIGATION_CONTAINER_STYLE,
            sportTitle: {
                style: {
                    ...PALETTE.QUICK_NAVIGATIONS_FAVORITES_SPORT_TITLE_TEXT,
                },
            },
            inPlayMenu: {
                rootContainer: {
                    style: {
                        _mobile_padding: 8,
                    },
                },
                navigationMenuSkeleton: {
                    linkPlaceholder: generateSkeletonPart(
                        PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR_SKELETON_PART
                    ),
                },
                navigationMenu: generateToggleButtonRow(PALETTE.QUICK_NAVIGATIONS_INPLAY_SPORTS_SELECTOR),
            },
            groupHeaderContainer: NAVIGATION_TITLE_CONTAINER_STYLE,
            countryOrSportIcon: NAVIGATION_TITLE_COUNTRY_ICON_STYLE,
            groupHeaderLabel: NAVIGATION_TITLE_STYLE,
            eventItemsContainer: NAVIGATION_ITEMS_CONTAINER_STYLE,
            eventItem: generateMiniEventItem(PALETTE.EVENTS_MINI_EVENT_ITEM),
            favoritesContentOnboarding: {
                header: {
                    style: {
                        ...PALETTE.QUICK_NAVIGATIONS_FAVORITES_ONBOARDING_TITLE_TEXT,
                    },
                },
                message: {
                    style: {
                        ...PALETTE.QUICK_NAVIGATIONS_FAVORITES_ONBOARDING_MESSAGE_TEXT,
                    },
                },
                closeButton: merge(
                    generateCloseButton(
                        PALETTE.QUICK_NAVIGATIONS_FAVORITES_ONBOARDING_HIDE_BUTTON,
                        CLOSE_BUTTON_TYPES.COMMON
                    ),
                    {
                        buttonIcon: {
                            style: {
                                width: 8,
                                height: 8,
                                minWidth: 8,
                                minHeight: 8,
                            },
                        },
                    }
                ),
            },
        },
        QuickLeaguesNavigation: {
            container: NAVIGATION_CONTAINER_STYLE,
            headerContainer: NAVIGATION_TITLE_CONTAINER_STYLE,
            countryOrSportIcon: NAVIGATION_TITLE_COUNTRY_ICON_STYLE,
            headerLabel: NAVIGATION_TITLE_STYLE,
            leaguesContainer: NAVIGATION_ITEMS_CONTAINER_STYLE,
            leagueItem: {
                button: {
                    style: {
                        base: {
                            ...resolveBackground(PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_BG),
                        },
                        active: {
                            ...resolveBackground(PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_BG),
                        },
                    },
                },
                buttonLabel: {
                    style: {
                        ...PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_PARTICIPANTS
                            .PARTICIPANTS_TEXT,
                    },
                },
                buttonLabelActive: {
                    style: {
                        ...PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT.MINI_EVENT_ITEM_PARTICIPANTS
                            .PARTICIPANTS_TEXT,
                    },
                },
                buttonIcon: icon(
                    PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
                buttonIconActive: icon(
                    PALETTE.LEAGUES_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
            },
        },
        QuickMeetingsNavigation: {
            container: NAVIGATION_CONTAINER_STYLE,
            headerContainer: NAVIGATION_TITLE_CONTAINER_STYLE,
            countryOrSportIcon: NAVIGATION_TITLE_COUNTRY_ICON_STYLE,
            headerLabel: NAVIGATION_TITLE_STYLE,
            meetingsContainer: NAVIGATION_ITEMS_CONTAINER_STYLE,
            meetingItem: {
                button: {
                    style: {
                        base: {
                            ...resolveBackground(PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_BG),
                        },
                        active: {
                            ...resolveBackground(PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_BG),
                        },
                    },
                },
                buttonLabel: {
                    style: {
                        ...PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_PARTICIPANTS
                            .PARTICIPANTS_TEXT,
                    },
                },
                buttonLabelActive: {
                    style: {
                        ...PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT
                            .MINI_EVENT_ITEM_PARTICIPANTS.PARTICIPANTS_TEXT,
                    },
                },
                buttonIcon: icon(
                    PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
                buttonIconActive: icon(
                    PALETTE.MEETINGS_MINI_EVENT_ITEM.MINI_EVENT_ITEM_SELECTED_CONTENT.MINI_EVENT_ITEM_CHEVRON_ICON_COLOR
                ),
            },
        },
    };
}
